import { AccessLevel } from "@types";

import {
    ATE_URL,
    DRUGBANK_URL,
    EVENT_URL,
    IOD_URL,
    LEARNING_CENTER_URL,
    MEDIA_CENTER_URL,
    MYJMC_URL,
    NEWS_CENTER_URL,
    PODCAST_CENTER_URL,
    PRODUCT_GLOSSARY_URL,
    PUBMED_URL,
    REQUEST_MATERIALS_URL,
    SCIENTIFIC_PUBLICATIONS_URL,
    SPECIALTIES_URL,
    TCP_URL,
    TOV_URL,
} from "../../plugins/JMCPageCreator/Constants";

export interface DefaultServiceProperties {
    url: string;
    overrideByInternalReference: boolean;
    mapping: string;
    relation?: string;
    title?: string;
    default_access_level?: AccessLevel;
}

export interface DefaultServices {
    ask_the_experts: DefaultServiceProperties;
    drug_to_drug: DefaultServiceProperties;
    event_center: DefaultServiceProperties;
    iod: DefaultServiceProperties;
    learning_center: DefaultServiceProperties;
    media_center: DefaultServiceProperties;
    myjmc: DefaultServiceProperties;
    news_center: DefaultServiceProperties;
    podcast_center: DefaultServiceProperties;
    product_glossary: DefaultServiceProperties;
    pubmed: DefaultServiceProperties;
    request_materials: DefaultServiceProperties;
    scientific_publications: DefaultServiceProperties;
    specialties: DefaultServiceProperties;
    tcp: DefaultServiceProperties;
    tov: DefaultServiceProperties;
    [key: string]: DefaultServiceProperties;
}

/**
 * Contains some required default values for the generation of
 * the service overview page cards for internal/default services.
 *
 * Can also be used to get the URLs etc.
 */
export const getDefaultServiceProperties = (): DefaultServices => ({
    request_materials: {
        url: REQUEST_MATERIALS_URL,
        overrideByInternalReference: false,
        mapping: "Contentstack_request_materials",
        default_access_level: AccessLevel.level3,
    },
    iod: {
        url: IOD_URL,
        overrideByInternalReference: false,
        mapping: "Contentstack_information_on_demand",
    },
    media_center: {
        url: MEDIA_CENTER_URL,
        overrideByInternalReference: false,
        mapping: "Contentstack_media_center",
    },
    product_glossary: {
        url: PRODUCT_GLOSSARY_URL,
        overrideByInternalReference: false,
        mapping: "Contentstack_product_glossary",
        relation: "Contentstack_product",
        title: "Products",
    },
    news_center: {
        url: NEWS_CENTER_URL,
        overrideByInternalReference: false,
        mapping: "Contentstack_news_center",
    },
    podcast_center: {
        url: PODCAST_CENTER_URL,
        overrideByInternalReference: false,
        mapping: "Contentstack_podcast_center",
    },
    specialties: {
        url: SPECIALTIES_URL,
        overrideByInternalReference: false,
        mapping: "Contentstack_specialties",
        relation: "Contentstack_specialty",
        title: "Specialties",
    },
    tov: {
        url: TOV_URL,
        overrideByInternalReference: false,
        mapping: "Contentstack_transfer_of_value",
    },
    pubmed: {
        url: PUBMED_URL,
        overrideByInternalReference: false,
        mapping: "Contentstack_pubmed",
    },
    myjmc: {
        url: MYJMC_URL,
        overrideByInternalReference: false,
        mapping: "Contentstack_myjmc",
        default_access_level: AccessLevel.level3,
    },
    learning_center: {
        url: LEARNING_CENTER_URL,
        overrideByInternalReference: false,
        mapping: "Contentstack_learning_center",
        default_access_level: AccessLevel.level3,
    },
    tcp: {
        url: TCP_URL,
        overrideByInternalReference: false,
        mapping: "Contentstack_tcp",
        default_access_level: AccessLevel.level1,
    },
    event_center: {
        url: EVENT_URL,
        overrideByInternalReference: false,
        mapping: "Contentstack_event_center",
        default_access_level: AccessLevel.level3,
        relation: "Contentstack_event",
        title: "Events",
    },
    scientific_publications: {
        url: SCIENTIFIC_PUBLICATIONS_URL,
        overrideByInternalReference: false,
        mapping: "Contentstack_scientific_publications",
        default_access_level: AccessLevel.level3,
    },
    ask_the_experts: {
        url: ATE_URL,
        overrideByInternalReference: false,
        mapping: "Contentstack_ask_the_experts",
        default_access_level: AccessLevel.level3,
    },
    drug_to_drug: {
        url: DRUGBANK_URL,
        overrideByInternalReference: false,
        mapping: "Contentstack_drug_to_drug",
        default_access_level: AccessLevel.level3,
    },
});
